/**
 * useSiteGeneration — drives the AI Website Generation flow from the UI.
 *
 * Responsibilities:
 *  - Shape the report into the backend generator's source payload and POST it
 *    (`siteBuilderApi.generate`). The Go backend owns the entire pipeline and persists
 *    the result; the frontend never plans, generates, or stores a schema.
 *  - Play staged, human-paced progress ("Analyzing your business…" → …) so the flow
 *    feels like real AI generation, independent of how fast the backend responds.
 *  - Navigate to /builder/:builderId using the backend-issued id.
 *
 * The staged progress is UI presentation only — it does NOT drive the backend.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReportPayload } from '../../types'
import { useI18nNavigate, useActiveLang } from '../../hooks/useI18nNavigate'
import { siteBuilderApi } from '../../api/client'
import { reportToSource } from '../services/reportToSource'
import type { WebsiteLocale } from '../schema'

/**
 * The visible pipeline steps. Each `key` maps to an i18n label under
 * `builder:generating.steps.<key>`. Durations tune the perceived pacing.
 */
export interface GenerationStep {
  key: string
  durationMs: number
}

export const GENERATION_STEPS: readonly GenerationStep[] = [
  { key: 'analyze', durationMs: 1200 }, // Business Report → Business Profile
  { key: 'profile', durationMs: 1000 }, // Understanding brand & assets
  { key: 'plan', durationMs: 1400 }, // AI Site Planner
  { key: 'schema', durationMs: 1200 }, // Site Schema assembly
  { key: 'design', durationMs: 900 }, // Preparing the design/preview
] as const

const TOTAL_MS = GENERATION_STEPS.reduce((sum, s) => sum + s.durationMs, 0)

export type GenerationStatus = 'idle' | 'generating' | 'done' | 'error'

export interface UseSiteGenerationResult {
  status: GenerationStatus
  /** Index into GENERATION_STEPS of the currently displayed step. */
  stepIndex: number
  /** Overall progress 0–100. */
  progress: number
  error: string | null
  /**
   * Begin generation. No-op while already generating. With `{ regenerate: true }` the
   * backend overwrites the business's existing builder in place; otherwise an existing
   * builder is reused.
   */
  start: (opts?: { regenerate?: boolean }) => void
}

const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

export function useSiteGeneration(report: ReportPayload): UseSiteGenerationResult {
  const navigate = useI18nNavigate()
  const locale = useActiveLang()

  const [status, setStatus] = useState<GenerationStatus>('idle')
  const [stepIndex, setStepIndex] = useState(0)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)

  // Track timers/intervals so we can clean up on unmount or completion.
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])
  const interval = useRef<ReturnType<typeof setInterval> | null>(null)
  const startedAt = useRef<number>(0)
  const mounted = useRef(true)

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout)
    timers.current = []
    if (interval.current !== null) {
      clearInterval(interval.current)
      interval.current = null
    }
  }, [])

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      clearTimers()
    }
  }, [clearTimers])

  const start = useCallback((opts?: { regenerate?: boolean }) => {
    if (status === 'generating') return
    setStatus('generating')
    setStepIndex(0)
    setProgress(0)
    setError(null)
    startedAt.current = Date.now()

    // Schedule step-label advances at cumulative offsets.
    let acc = 0
    GENERATION_STEPS.forEach((step, i) => {
      timers.current.push(setTimeout(() => {
        if (mounted.current) setStepIndex(i)
      }, acc))
      acc += step.durationMs
    })

    // Smoothly advance the progress bar based on elapsed time (cap < 100 until done).
    interval.current = setInterval(() => {
      if (!mounted.current) return
      const elapsed = Date.now() - startedAt.current
      setProgress(Math.min(99, Math.round((elapsed / TOTAL_MS) * 100)))
    }, 60)

    // Generate on the backend (the pipeline runs + persists there) and run the
    // minimum staged duration in parallel. The frontend never fabricates or stores a
    // schema — it receives a builder id and navigates; the builder is fetched from the
    // backend on the next page.
    const websiteLocale: WebsiteLocale = locale === 'ar' ? 'ar' : 'en'
    void (async () => {
      try {
        const source = reportToSource(report, websiteLocale)
        const [result] = await Promise.all([
          siteBuilderApi.generate({
            source,
            businessId: report.business_id,
            locale: websiteLocale,
            regenerate: opts?.regenerate,
          }),
          delay(TOTAL_MS),
        ])
        if (!mounted.current) return
        clearTimers()
        setProgress(100)
        setStepIndex(GENERATION_STEPS.length - 1)
        setStatus('done')
        // Hand off to the builder route using the backend-issued builder id. Pass the
        // scan id along so the Builder's Back button can return to THIS exact report
        // (see useGoToReport) rather than guessing the business's most recent scan.
        navigate(`/builder/${result.builderId}`, { state: { scanId: report.scan_id } })
      } catch {
        if (!mounted.current) return
        clearTimers()
        setStatus('error')
        setError('generation_failed')
      }
    })()
  }, [status, report, locale, navigate, clearTimers])

  return { status, stepIndex, progress, error, start }
}
